import axiosInstance from './axiosInstance';
import {
  ConnectionDataResponseDto,
  MassMessageRequestDto,
  ProfessionalChatUpdateDto,
} from '@docbay/schemas';
import { StartTeleconsultationRequest } from 'applicaiton/store/reducers/Chats/models';
import axiosBlobInstance from './axiosBlobInstance';

export interface ChatsSearchParams {
  topicName?: string;
  participantName?: string;
  topicIds?: string[];
}

/**
 * A class that provides methods for interacting with the Chats API.
 */
export const ChatsAPI = {
  /**
   * Retrieves a list of chats based on the given search parameters.
   * @param requestParams - An object containing the search parameters.
   * @returns A promise that resolves to an array of ConnectionDataResponseDto objects.
   */
  getChats: (requestParams: ChatsSearchParams) => {
    const params = {
      ...requestParams,
      topicIds: requestParams.topicIds?.join(','),
    };
    return axiosInstance.get('/pro-side/professional-chats', { params });
  },

  /**
   * Generates an access token for a chat.
   * @returns A promise that resolves to a ConnectionDataResponseDto object.
   */
  getAccessToken: () => {
    return axiosInstance.post(
      '/pro-side/professional-chats/generate-access-token',
    );
  },

  /**
   * Creates a new chat with the given patient ID.
   * @param patientId - The ID of the patient to create the chat for.
   * @returns A promise that resolves to a ConnectionDataResponseDto object.
   */
  createChat: (patientId: string) => {
    return axiosInstance.post('/pro-side/professional-chats', { patientId });
  },

  /**
   * Retrieves a chat by its conversation SID.
   * @param conversationSID - The SID of the conversation to retrieve.
   * @returns A promise that resolves to a ConnectionDataResponseDto object.
   */
  getChatBySID: (conversationSID: string) => {
    return axiosInstance.get(`/pro-side/professional-chats/${conversationSID}`);
  },

  /**
   * Updates the given chat with the provided data.
   * @param conversationSID - The SID of the conversation to update.
   * @param data - An object containing the updated data for the chat.
   * @returns A promise that resolves to a ConnectionDataResponseDto object.
   */
  updateChat: (conversationSID: string, data: ProfessionalChatUpdateDto) => {
    return axiosInstance.patch(
      `/pro-side/professional-chats/${conversationSID}`,
      data,
    );
  },

  /**
   * Archives the given chat.
   * @param conversationSID - The SID of the conversation to archive.
   * @returns A promise that resolves to a ConnectionDataResponseDto object.
   */
  archiveChat: (conversationSID: string) => {
    return axiosInstance.patch(
      `/pro-side/professional-chats/${conversationSID}/archive`,
    );
  },

  /**
   * Unarchives the given chat.
   * @param conversationSID - The SID of the conversation to unarchive.
   * @returns A promise that resolves to a ConnectionDataResponseDto object.
   */
  unArchiveChat: (conversationSID: string) => {
    return axiosInstance.patch(
      `/pro-side/professional-chats/${conversationSID}/unarchive`,
    );
  },

  /**
   * Deletes the given chat.
   * @param conversationSID - The SID of the conversation to delete.
   * @returns A promise that resolves to a ConnectionDataResponseDto object.
   */
  deleteChat: (conversationSID: string) => {
    return axiosInstance.delete(
      `/pro-side/professional-chats/${conversationSID}`,
    );
  },

  /**
   * Posts a mass message to the given chat.
   * @param data - An object containing the mass message to post.
   * @returns A promise that resolves to a ConnectionDataResponseDto object.
   */
  postMassMessagesChat: (data: { params: MassMessageRequestDto }) => {
    return axiosInstance.post(
      '/pro-side/professional-chats/mass-message',
      data.params,
    );
  },

  /**
   * Deletes the given mass message from the chat.
   * @param massMessageId - The ID of the mass message to delete.
   * @returns A promise that resolves to a ConnectionDataResponseDto object.
   */
  deleteMassMessage: (massMessageId: string) => {
    return axiosInstance.delete(
      `/pro-side/professional-chats/mass-message/${massMessageId}`,
    );
  },

  /**
   * Starts a teleconsultation for the given chat.
   *
   * @param request - An object containing the SID of the conversation and an optional patient message to update.
   *
   * @returns A promise that resolves to a ConnectionDataResponseDto object.
   */
  startChatTeleconsultation: ({
    conversationSid,
    patientMessageToUpdate,
  }: StartTeleconsultationRequest) => {
    const data = patientMessageToUpdate
      ? {
          patientMessageToUpdate,
        }
      : {};
    return axiosInstance.post<ConnectionDataResponseDto>(
      `/pro-side/professional-chats/${conversationSid}/start-teleconsultation`,
      data,
    );
  },

  /**
   * Adds a document message to the given chat.
   *
   * @param request - An object containing the SID of the conversation and an FormData object representing the document to upload.
   *
   * @returns A promise that resolves to a ConnectionDataResponseDto object.
   */
  addDocumentMessage: ({
    conversationSID,
    data,
  }: {
    conversationSID: string;
    data: FormData;
  }) => {
    return axiosInstance.post(
      `/pro-side/professional-chats/document-message/${conversationSID}/upload`,
      data,
    );
  },

  /**
   * Deletes the given document message from the chat.
   *
   * @param messageSid - The SID of the message to delete.
   *
   * @returns A promise that resolves to a ConnectionDataResponseDto object.
   */
  deleteDocumentMessage: (messageSid: string) => {
    return axiosInstance.delete(
      `/pro-side/professional-chats/document-message/${messageSid}`,
    );
  },

  /**
   * Downloads the given document message from the chat.
   *
   * @param messageSid - The SID of the message to download.
   *
   * @returns A promise that resolves to a Blob object containing the downloaded document.
   */
  downloadDocument: (messageSid: string) => {
    return axiosBlobInstance.get(
      `/pro-side/professional-chats/document-message/${messageSid}/download`,
    );
  },

  /**
   * Closes the topic of the given chat.
   *
   * @param conversationSid - The SID of the conversation to close the topic for.
   *
   * @returns A promise that resolves to a ConnectionDataResponseDto object.
   */
  closeTopic: (conversationSid: string) => {
    return axiosInstance.patch(
      `/pro-side/professional-chats/${conversationSid}/close-topic`,
    );
  },

  /**
   * Creates an access token for the given chat room ID.
   *
   * @param roomId - The ID of the chat room to generate the access token for.
   *
   * @returns A promise that resolves to a ConnectionDataResponseDto object containing the generated access token.
   */
  createTeleconsultationTokenByRoomId: (roomId: string) =>
    axiosInstance.post(
      `/pro-side/chat-teleconsultation/${roomId}/generate-access-token`,
    ),
};
